export class RGBA {
  r: number
  g: number
  b: number
  a: number

  constructor(r: number, g: number, b: number, a: number) {
    this.r = r
    this.g = g
    this.b = b
    this.a = a
  }

  toString() {
    return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`
  }

  withWhiteBackground() {
    return new RGBA(
      Math.round(this.r + (255 - this.r) * this.a),
      Math.round(this.g + (255 - this.g) * this.a),
      Math.round(this.b + (255 - this.b) * this.a),
      1
    )
  }

  /**
   * Converts the color to a transparent color that would render the same on a white background.
   * @returns color with alpha channel
   */
  transparent() {
    const higest = Math.max(this.r, this.g, this.b)
    const factor = 255 / higest
    return new RGBA(
      Math.round(this.r * factor),
      Math.round(this.g * factor),
      Math.round(this.b * factor),
      factor
    )
  }

  static transparent(): RGBA {
    return new RGBA(0, 0, 0, 0)
  }

  lighten(factor: number): RGBA {
    const [h, s, l] = this.toHSL()
    return this.fromHSL(h, s, Math.min(1, l + factor), this.a)
  }

  darken(factor: number): RGBA {
    const [h, s, l] = this.toHSL()
    return this.fromHSL(h, s, Math.max(0, l - factor), this.a)
  }

  private toHSL(): [number, number, number] {
    const r = this.r / 255
    const g = this.g / 255
    const b = this.b / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const delta = max - min
    const l = (max + min) / 2

    if (delta === 0) return [0, 0, l]

    const s = l < 0.5 ? delta / (max + min) : delta / (2 - max - min)

    let h
    switch (max) {
      case r:
        h = ((g - b) / delta + (g < b ? 6 : 0)) * 60
        break
      case g:
        h = ((b - r) / delta + 2) * 60
        break
      case b:
        h = ((r - g) / delta + 4) * 60
        break
    }

    return [h, s, l]
  }

  private fromHSL(h: number, s: number, l: number, a: number): RGBA {
    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2

    let [r, g, b] = [0, 0, 0]
    if (h < 60) [r, g, b] = [c, x, 0]
    else if (h < 120) [r, g, b] = [x, c, 0]
    else if (h < 180) [r, g, b] = [0, c, x]
    else if (h < 240) [r, g, b] = [0, x, c]
    else if (h < 300) [r, g, b] = [x, 0, c]
    else if (h < 360) [r, g, b] = [c, 0, x]

    return new RGBA(
      Math.round((r + m) * 255),
      Math.round((g + m) * 255),
      Math.round((b + m) * 255),
      a
    )
  }

  static white(): RGBA {
    return new RGBA(255, 255, 255, 1)
  }

  static black(): RGBA {
    return new RGBA(0, 0, 0, 1)
  }

  static fromHex(hex: string): RGBA {
    const hexWithoutHash = hex.replace('#', '')
    const l = hexWithoutHash.length
    if (hex === 'transparent') return RGBA.transparent()
    if (l === 3 || l === 4) return RGBA.fromHexShort(hexWithoutHash)
    if (l === 6 || l === 8) return RGBA.fromHexLong(hexWithoutHash)
    throw new Error(`Invalid hex color: ${hex}`)
  }

  static fromRgb(color: string): RGBA {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(,\s*(\d+(\.\d+)?))?\)/)
    if (!match) throw new Error(`Invalid rgb color: ${color}`)
    return new RGBA(
      parseInt(match[1]),
      parseInt(match[2]),
      parseInt(match[3]),
      parseFloat(match[5] || '1')
    )
  }

  private static fromHexShort(hex: string): RGBA {
    const r = parseInt(hex.substring(0, 1), 16)
    const g = parseInt(hex.substring(1, 2), 16)
    const b = parseInt(hex.substring(2, 3), 16)
    const a = hex.length == 4 ? parseInt(hex.substring(3, 4), 16) / 16 : 1
    return new RGBA(r * 17, g * 17, b * 17, a)
  }

  private static fromHexLong(hex: string): RGBA {
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    let a = 1
    if (hex.length == 8) {
      const i = parseInt(hex.substring(6, 8), 16)
      a = i === 255 ? 1 : i / 256
    }
    return new RGBA(r, g, b, a)
  }

  toHex(): string {
    const format = (n: number) => n.toString(16).padStart(2, '0')
    const a = Math.round(this.a * 256)
    return `#${format(this.r)}${format(this.g)}${format(this.b)}${format(a === 256 ? 255 : a)}`
  }
}

export function colorBetween(a: RGBA, b: RGBA, ratio: number): RGBA {
  return new RGBA(
    Math.round(a.r + (b.r - a.r) * ratio),
    Math.round(a.g + (b.g - a.g) * ratio),
    Math.round(a.b + (b.b - a.b) * ratio),
    a.a + (b.a - a.a) * ratio
  )
}

export function lightenColor(color: string, factor: number): string {
  return RGBA.fromHex(color).lighten(factor).toHex()
}

export function darkenColor(color: string, factor: number): string {
  return RGBA.fromHex(color).darken(factor).toHex()
}

export const ANT_DESIGN_COLORS = {
  'gray-1': RGBA.fromHex('#FFFFFF'),
  'gray-2': RGBA.fromHex('#FAFAFA'),
  'gray-3': RGBA.fromHex('#F5F5F5'),
  'gray-4': RGBA.fromHex('#F0F0F0'),
  'gray-5': RGBA.fromHex('#D9D9D9'),
  'gray-6': RGBA.fromHex('#BFBFBF'),
  'gray-7': RGBA.fromHex('#8C8C8C'),
  'gray-8': RGBA.fromHex('#595959'),
  'gray-9': RGBA.fromHex('#434343'),
  'gray-10': RGBA.fromHex('#262626'),
  'gray-11': RGBA.fromHex('#1F1F1F'),
  'gray-12': RGBA.fromHex('#141414'),
  'gray-13': RGBA.fromHex('#000000'),

  'blue-1': RGBA.fromHex('#E6F7FF'),
  'blue-2': RGBA.fromHex('#BAE7FF'),
  'blue-3': RGBA.fromHex('#91D5FF'),
  'blue-4': RGBA.fromHex('#69C0FF'),
  'blue-5': RGBA.fromHex('#40A9FF'),
  'blue-6': RGBA.fromHex('#1890FF'),
  'blue-7': RGBA.fromHex('#096DD9'),
  'blue-8': RGBA.fromHex('#0050B3'),
  'blue-9': RGBA.fromHex('#003A8C'),
  'blue-10': RGBA.fromHex('#002766'),

  'cyan-1': RGBA.fromHex('#E6FFFB'),
  'cyan-2': RGBA.fromHex('#B5F5EC'),
  'cyan-3': RGBA.fromHex('#87E8DE'),
  'cyan-4': RGBA.fromHex('#5CDBD3'),
  'cyan-5': RGBA.fromHex('#36CFC9'),
  'cyan-6': RGBA.fromHex('#13C2C2'),
  'cyan-7': RGBA.fromHex('#08979C'),
  'cyan-8': RGBA.fromHex('#006D75'),
  'cyan-9': RGBA.fromHex('#00474F'),
  'cyan-10': RGBA.fromHex('#002329'),

  'purple-1': RGBA.fromHex('#F9F0FF'),
  'purple-2': RGBA.fromHex('#EFDBFF'),
  'purple-3': RGBA.fromHex('#D3ADF7'),
  'purple-4': RGBA.fromHex('#B37FEB'),
  'purple-5': RGBA.fromHex('#9254DE'),
  'purple-6': RGBA.fromHex('#722ED1'),
  'purple-7': RGBA.fromHex('#531DAB'),
  'purple-8': RGBA.fromHex('#391085'),
  'purple-9': RGBA.fromHex('#22075E'),
  'purple-10': RGBA.fromHex('#120338'),

  'magenta-1': RGBA.fromHex('#FFF0F6'),
  'magenta-2': RGBA.fromHex('#FFD6E7'),
  'magenta-3': RGBA.fromHex('#FFADD2'),
  'magenta-4': RGBA.fromHex('#FF85C0'),
  'magenta-5': RGBA.fromHex('#F759AB'),
  'magenta-6': RGBA.fromHex('#EB2F96'),
  'magenta-7': RGBA.fromHex('#C41D7F'),
  'magenta-8': RGBA.fromHex('#9E1068'),
  'magenta-9': RGBA.fromHex('#780650'),
  'magenta-10': RGBA.fromHex('#520339')
}
